import React from 'react';
import { useIntl } from 'react-intl';
import { Helmet } from 'react-helmet';
import { Redux, Slices } from 'spwa-ui-library';
import { longToShortCountryCode } from 'spwa-ui-library/esm/utils/helper';

const { useSelector } = Redux;
const { selectCurrentCountry } = Slices.profileSlice;
const { selectCountryValidations } = Slices.configSlice;
const SeoTitle = ({
  id,
  submenuname = '',
  productname = '',
  storename = '',
  // countryName,
  cityname = '',
  storeDetails = undefined,
  opneingHours = '',
  overRideMeta = null,
}) => {
  const intl = useIntl();
  const countryName = useSelector(selectCurrentCountry);
  const countryValidations = useSelector((state) => selectCountryValidations(state, countryName));
  if (!id) return '';

  const addStoreDetails = () => {
    if (!storeDetails) return null;

    let storeStartTimeArray = (storeDetails[`startTime`] || '').split('T')?.[1]?.split(':') || [];
    let storeEndTimeArray = (storeDetails[`endTime`] || '').split('T')?.[1]?.split(':') || [];

    const storeStartTime = `${storeStartTimeArray?.[0] || '00'}:${
      storeStartTimeArray?.[1] || '00'
    }`;
    const storeEndTime = `${storeEndTimeArray?.[0] || '23'}:${storeEndTimeArray?.[1] || '59'}`;

    return (
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'LocalBusiness',
          name: storeDetails[`name_${intl.locale}`] || '',
          image: '',
          '@id': `${storeDetails[`storeId`] || ''}`,
          url: window.location.href,
          telephone: storeDetails[`phone1`] || '',
          address: {
            '@type': 'PostalAddress',
            streetAddress: storeDetails[`address_${intl.locale}`] || '',
            addressLocality: '',
            postalCode: '',
            addressCountry: longToShortCountryCode[countryName] || countryName || '',
          },
          openingHoursSpecification: {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: [
              'Monday',
              'Tuesday',
              'Wednesday',
              'Thursday',
              'Friday',
              'Saturday',
              'Sunday',
            ],
            opens: storeStartTime,
            closes: storeEndTime,
          },
        })}
      </script>
    );
  };

  const keywordMapping = {
    uae: {
      en: 'Abu Dhabi, Dubai, Sharjah',
      ar: 'أبو ظبي,دبي,الشارقة',
    },
    ksa: {
      en: 'Riyadh, Makkah, Jeddah',
      ar: 'مدينة الرياض,مكة,جدة',
    },
    egy: {
      en: 'Cairo, Giza, Alex',
      ar: 'القاهرة,جيزة,الإسكندرية',
    },
    kwt: {
      en: 'Kuwait City, Jahra, Hawally',
      ar: 'العاصمه,الجهرا,حولي',
    },
    qat: {
      en: 'Doha, Khour, Wakrah',
      ar: 'الدوحة,الخور,الوكرة',
    },
    bhr: {
      en: 'Bahrain',
      ar: 'البحرين',
    },
    omn: {
      en: 'Muscat,Sohar',
      ar: 'مَسْقَط,صُحَار',
    },
  };

  let title = intl.formatMessage(
    { id: `seo_` + id + `_title` },
    {
      submenuname,
      productname,
      storename,
      cityname,
      countryName,
      opneingHours,
      contactNumber: countryValidations?.customerCare,
    }
  );
  let key = intl.formatMessage(
    { id: `seo_` + id + `_key` },
    {
      submenuname,
      productname,
      storename,
      cityname,
      citiesData: keywordMapping[countryName.toLowerCase()][intl.locale],
      countryName,
      opneingHours,
      contactNumber: countryValidations?.customerCare,
    }
  );
  let desc = intl.formatMessage(
    { id: `seo_` + id + `_desc` },
    {
      submenuname,
      productname,
      storename,
      cityname,
      countryName,
      opneingHours,
      contactNumber: countryValidations?.customerCare,
    }
  );
  const metaMap = {
    deals: {
      title: 'Pizza Deals Near You | Offers & Promotions' + '| Pizza Hut ' + countryName,
      desc:
        "Satisfy your cravings with our delicious deals! From BOGO offers to family feasts, order online now and enjoy great savings on Pizza Hut UAE's menu.",
      key:
        'pizza hut uae deals,pizza hut wow triples ,pizza hut big box deal ,pizza hut buy one get one free deal,pizza hut 2 medium pizza deal,pizza hut 3 medium pizza deal',
    },
    pizza: {
      title: 'Order Your Favorite Pizza Online' + '| Pizza Hut ' + countryName,
      desc:
        'Craving pizza? Look no further than Pizza Hut UAE! Order online now and enjoy our delicious menu of classic and innovative handcrafted pizzas.',
      key:
        'spicy tandoori paneer pizza hut,pizza hut ultimate cheese pizza,pizza hut bbq chicken,pizza hut margherita',
    },
    starters: {
      title: 'Delicious Starters & Appetizers Online ' + '| Pizza Hut ' + countryName,
      desc:
        "Kick off your meal in style with our delicious starters! From cheesy bites to garlic bread, order online now and enjoy Pizza Hut UAE's menu.",
      key:
        'pizza starter, buffalo wings pizza hut,creamy ranch pizza hut,potato wedges pizza hut,chilli cheese nuggets pizza hut',
    },
    pasta: {
      title: 'Order Your Favorite Pasta Online ' + '| Pizza Hut ' + countryName,
      desc:
        'Take a break from pizza and try our mouth-watering pasta dishes from our menu! From classic spaghetti to creamy alfredo, order online now at Pizza Hut UAE.',
      key:
        'order dessert online,chocolate dessert pizza,order desserts online delivery,pizza hut dessert menu',
    },
    desserts: {
      title: 'Order Your Favorite Dessert Online ' + '| Pizza Hut ' + countryName,
      desc:
        'There is nothing that a dessert cannot fix! Order your favorite dessert online at Pizza Hut UAE. Browse through our dessert menu at Pizza Hut today!',
      key:
        'pizza hut dessert,order dessert online,chocolate dessert pizza, order desserts online delivery,pizza hut dessert menu',
    },
    drinks: {
      title: 'Order Your Favourite Drinks Online ' + '| Pizza Hut ' + countryName,
      desc:
        'Need a drink while having your pizzas and pasta? Enjoy refreshing drinks and fresh juices at Pizza Hut UAE. Order online now!',
      key: 'pizza hut drinks,pizza hut drinks menu ',
    },

    forme: {
      title: 'Meal for One | Order Your Solo Meal' + '| Pizza Hut ' + countryName,
      desc:
        'Enjoy our meal for one that includes a small pizza of your choice, a choice of potato wedges or garlic bread, a dip, and a soft drink. Order online now!',
      key: 'meal for one pizza hut,pizza hut meal for one,pizza hut solo meal',
    },

    extras: {
      title: 'Order Delicious Extas Online | Pizza Hut ' + countryName,
      desc:
        'Enhance your pizza experience with mouthwatering extras at Pizza Hut UAE. Discover a variety of delicious side dishes and more. Order now!',
      key: 'pizza hut extras',
    },

    buildyourownpizza: {
      title: 'Build Your Own Pizza Online | Pizza Hut ' + countryName,
      desc:
        'Get creative and build your own pizza masterpiece at Pizza Hut UAE. Choose your toppings, sauce, and cheese. Order now for a personalized delight!',
      key: 'pizza hut build your own pizza',
    },

    myfavorites: {
      title: 'My Favorites | Pizza Hut ' + countryName,
      desc:
        'Explore your favorites at Pizza Hut UAE. Discover a curated selection of delectable dishes that never disappoint. Order now for a trusted taste!',
      key: 'pizza hut my favroites',
    },
  };
  const metaMapAr = {
    العروض: {
      title: 'بيتزا هت الامارات | عروض البيتزا | عروض بيتزا هت',
      desc:
        'اطّلع على أفضل عروض بيتزا هت الامارات.البيتزا والباستا والسلطة والمشروبات والمزيد. اطلب البيتزا المفضلة لديك اونلاين واحصل عليها  بدون تلامس!',
      key: '',
    },
    البيتزا: {
      title: 'بيتزا هت الإمارات | بيتزا هت دليفري  - طلب بيتزا اونلاين',
      desc:
        'اطلب البيتزا المفضلة لديك واللذيذة عبر الإنترنت من بيتزا هت الإمارات. بيتزا دجاج باربيكيو ، بيتزا مارجريتا ، بيتزا التميت تشيز والمزيد. اطلب اونلاين الآن!',
      key: '',
    },
    المقبلات: {
      title: 'بيتزا هت الامارات | اطلب مقبلات بيتزا هت | مقبلات واطباق جانبية',
      desc:
        'اطلب مقبلات بيتزا هت الإمارات اونلاين، بطاطا ودجز، اجنحة الدجاج الحارة وتشيلي تشيز ناجتس او خبز بالثوم وغيرها الكثير. اطلب اونلاين الآن!',
      key: '',
    },
    الباستا: {
      title: 'اطلب باستا بيتزا هت اونلاين | بيتزا هت الامارات',
      desc:
        'هل تشتهي وجبة شهية؟ كل ما تحتاجه هو باستا بيتزا هت! استمتع بجميع انواع الباستا اللذيذة والطازجة من قائمة بيتزا هت الإمارات. اطلب اونلاين الآن!',
      key: '',
    },
    الحلويات: {
      title: 'اطلب حلويات بيتزا هت المفضلة لديك اونلاين | بيتزا هت الامارات',
      desc:
        'لا يوجد شيء لا يمكن للحلويات إصلاحه! اطلب الحلويات المفضلة لديك اونلاين من بيتزا هت الإمارات. تصفح قائمة الحلويات لدينا في بيتزا هت اليوم!',
      key: '',
    },
    المشروبات: {
      title: 'اطلب مشروبات بيتزا هت المفضلة اونلاين | بيتزا هت الامارات',
      desc:
        'هل تحتاج إلى مشروب اثناء تناول البيتزا والباستا؟ استمتع بجموعة متنوعة من المشروبات المنعشة والعصائر الطازجة من بيتزا هت الإمارات. اطلب اونلاين الآن!',
      key: '',
    },
    إضافات: {
      title: 'طلب اضافات بيتزا هت اللذيذة اونلاين | بيتزا هت ' + countryName,
      desc:
        'حسن تجربة البيتزا الخاصة بك مع الإضافات الشهية من بيتزا هت الإمارات. اكتشف مجموعة متنوعة من الإضافات الجانبية اللذيذة . اطلبها اونلاين الآن!',
      key: 'اضافات بيتزا هت',
    },

    جهزالبيتزاالخاصةبك: {
      title: 'جهز البيتزا الخاصة بك اونلاين | بيتزا هت ' + countryName,
      desc:
        'كن مبدعاً وقم بإختيار مكونات البيتزا الخاصة بك في بيتزا هت الإمارات. اختر الطبقة الخاصة بك، والصلصة، والجبن وقم بتكوين البيتزا بنفسك! اطلب الآن!',
      key: 'جهز البيتزا الخاصة بك',
    },

    myfavorites: {
      title: 'مفضلاتي | قم بحفظ اختياراتك | بيتزا هت ' + countryName,
      desc:
        'قم بحفظ اختياراتك من قائمة بيتزا هت الإمارات الشهية في مكاناً واحد حتى لا تضيع وقت في اللإختيار كل مرة عليك طلب بيتزا لذيذة. قم بطلب وجبة لذيذة الآن!',
      key: 'مفضلاتي',
    },
  };
  if (submenuname !== '' && productname === '') {
    const mainMap = intl.locale === 'ar' ? metaMapAr : metaMap;
    const subMenuValue = submenuname?.toLowerCase().replace(' ', '');
    if (mainMap[subMenuValue]) {
      title = mainMap[subMenuValue].title;
      key = mainMap[subMenuValue].key;
      desc = mainMap[subMenuValue].desc;
    }
  }
  if (overRideMeta) {
    let overRideTitle = null;
    if (overRideMeta?.metaTitle) {
      overRideTitle = overRideMeta?.metaTitle?.includes(intl.formatMessage({ id: 'home_h1' }))
        ? overRideMeta?.metaTitle
        : overRideMeta?.metaTitle + ` |  ${intl.formatMessage({ id: 'home_h1' })} ${countryName}`;
    }
    title = overRideTitle || title;
    key = (overRideMeta?.metaKeyword?.length && overRideMeta?.metaKeyword[0]) || key;
    desc = overRideMeta?.metaDesc || overRideMeta?.desc || overRideMeta?.description || desc;
  }

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="keywords" content={key} />
      <meta name="description" content={desc} />
      {addStoreDetails()}
    </Helmet>
  );
};

export default SeoTitle;
